import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../lib/api';
import { User, Layers, Check, X, ChevronUp, MessageCircle, Wifi, Zap, AlertTriangle, Users, History, ArrowLeft } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const getToken = () =>
  localStorage.getItem('retailcliq_token') ||
  sessionStorage.getItem('retailcliq_token') ||
  '';

const Taskbar = ({ screens, activeScreenId, onScreenSelect, onScreenClose, onGoBack, canGoBack, userName, theme = 'light', onSmartAssistantToggle, isSmartAssistantOpen, onSystemLogToggle, isSystemLogOpen, onTableViewToggle, isTableViewOpen, companyName, fiscalYear, branchName }) => {
  const [time, setTime] = useState(new Date());
  const [isWindowMenuOpen, setIsWindowMenuOpen] = useState(false);
  const [inboxBadge, setInboxBadge] = useState({ totalOpen: 0, newSinceLogin: 0, newSinceLastOpened: 0 });
  const isDark = theme === 'dark';
  const menuRef = useRef(null);
  const prevIsOpenRef = useRef(isSmartAssistantOpen);
  const prevBadgeRef = useRef(0);
  const lastOpenedRef = useRef(localStorage.getItem('sa_last_opened') || null);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // When panel transitions open → record the timestamp so next open resets "new since opened" count
  useEffect(() => {
    if (isSmartAssistantOpen && !prevIsOpenRef.current) {
      const now = new Date().toISOString();
      localStorage.setItem('sa_last_opened', now);
      lastOpenedRef.current = now;
    }
    prevIsOpenRef.current = isSmartAssistantOpen;
  }, [isSmartAssistantOpen]);

  // Poll inbox badge every 30 s
  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const lastOpened = lastOpenedRef.current;
        const params = lastOpened ? `?lastOpened=${encodeURIComponent(lastOpened)}` : '';
        const res = await fetch(`${API_BASE}/api/smart-assistant/inbox/badge${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.success) return;
        setInboxBadge(data);
        // Toast when new ticket arrives since last check (and panel is not open)
        if (!isSmartAssistantOpen && data.totalOpen > prevBadgeRef.current && prevBadgeRef.current >= 0) {
          const diff = data.totalOpen - prevBadgeRef.current;
          toast(`${diff} new ticket${diff > 1 ? 's' : ''} in Assistant inbox`, {
            description: `${data.totalOpen} open ticket${data.totalOpen > 1 ? 's' : ''} need attention`,
            action: { label: 'Open', onClick: onSmartAssistantToggle },
            duration: 6000,
          });
        }
        prevBadgeRef.current = data.totalOpen;
      } catch (_) { /* silent — badge is non-critical */ }
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmartAssistantOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setIsWindowMenuOpen(false);
    };
    if (isWindowMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isWindowMenuOpen]);

  // Previous screen (the one before active)
  const activeIdx = screens.findIndex(s => s.id === activeScreenId);
  const prevScreen = activeIdx > 0 ? screens[activeIdx - 1] : null;

  const Pipe = () => <div className={`w-px h-4 shrink-0 ${isDark ? 'bg-[#333]' : 'bg-slate-200'}`} />;

  return (
    <div data-testid="taskbar" className={`w-full h-10 sm:h-9 border-t flex items-center px-1.5 sm:px-2 z-[100] select-none transition-colors duration-300 shrink-0 safe-bottom ${isDark ? 'bg-[#0e0e10] border-[#222]' : 'bg-white border-slate-200 shadow-inner shadow-slate-100'}`}>

      {/* ── Screens (left) ── */}
      <div className="flex items-center" ref={menuRef}>
        <div className="relative">
          <button data-testid="taskbar-screens-btn"
            onClick={() => setIsWindowMenuOpen(!isWindowMenuOpen)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-tight transition-all touch-target ${
              isWindowMenuOpen ? 'bg-blue-600 text-white shadow' : isDark ? 'text-slate-500 hover:bg-white/5 hover:text-slate-300' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            }`}>
            <Layers size={13} />
            <span className="hidden sm:inline">Screens</span>
            <span className={`px-1 py-px rounded text-[9px] font-mono ${isWindowMenuOpen ? 'bg-blue-500 text-white' : isDark ? 'bg-[#222] text-slate-400' : 'bg-slate-200 text-slate-500'}`}>{screens.length}</span>
            <ChevronUp size={10} className={`hidden sm:block transition-transform ${isWindowMenuOpen ? '' : 'rotate-180'}`} />
          </button>

          {isWindowMenuOpen && (
            <div className={`absolute bottom-full left-0 mb-2 w-64 sm:w-72 border rounded-xl shadow-2xl z-[200] overflow-hidden ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-white border-slate-200 shadow-xl'}`}>
              <div className={`px-3 py-2 border-b ${isDark ? 'border-[#333] bg-[#151515]' : 'border-slate-100 bg-slate-50'}`}>
                <span className={`text-[9px] font-black uppercase tracking-[0.15em] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Open Windows</span>
              </div>
              <div className="max-h-[220px] sm:max-h-[280px] overflow-y-auto p-1 space-y-px">
                {screens.map((screen) => (
                  <div key={screen.id} data-testid={`taskbar-window-${screen.id}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors group touch-target ${
                      activeScreenId === screen.id
                        ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'
                        : isDark ? 'hover:bg-white/5 text-slate-400' : 'hover:bg-slate-50 text-slate-600'
                    }`}
                    onClick={() => { onScreenSelect(screen.id); setIsWindowMenuOpen(false); }}>
                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${activeScreenId === screen.id ? (isDark ? 'bg-white' : 'bg-blue-600') : isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
                    <span className="text-[11px] font-semibold uppercase truncate flex-1">{screen.title}</span>
                    {activeScreenId === screen.id && <Check size={12} />}
                    {screens.length > 1 && (
                      <button onClick={(e) => { e.stopPropagation(); onScreenClose(screen.id); }}
                        className="p-1 rounded hover:bg-red-500/20 hover:text-red-500 transition-all touch-target">
                        <X size={11} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Pipe />

      {/* ── Context Info: FY | Biz Day | Branch ── */}
      <div className={`hidden md:flex items-center text-[9px] font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`} data-testid="taskbar-context">
        {fiscalYear && <><span className="px-3">FY: {fiscalYear}</span><Pipe /></>}
        <span className="px-3">Biz Day: {time.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        <Pipe />
        {branchName && <><span className="px-3">{branchName}</span><Pipe /></>}
      </div>

      {/* ── Live Users ── */}
      <div className={`hidden md:flex items-center gap-1.5 px-3 text-[9px] font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`} data-testid="taskbar-live-users">
        <Users size={11} />
        <span>1 Online</span>
      </div>

      <Pipe />

      {/* ── Go Back (history stack) ── */}
      {canGoBack && (
        <>
          <button
            onClick={onGoBack}
            className={`hidden md:flex items-center gap-1.5 px-3 text-[10px] font-semibold transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            data-testid="taskbar-back-btn"
            title="Go back to previous screen"
          >
            <ArrowLeft size={12} />
            <span>Back</span>
          </button>
          <Pipe />
        </>
      )}

      {/* ── Previous Screen ── */}
      {prevScreen && (
        <>
          <button
            onClick={() => onScreenSelect(prevScreen.id)}
            className={`hidden md:flex items-center gap-1.5 px-3 text-[9px] font-medium transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
            data-testid="taskbar-prev-screen"
            title={`Previous: ${prevScreen.title}`}
          >
            <History size={11} />
            <span className="truncate max-w-[100px]">{prevScreen.title}</span>
          </button>
          <Pipe />
        </>
      )}

      {/* ── Spacer ── */}
      <div className="flex-1" />

      {/* ── Messages ── */}
      <button data-testid="taskbar-messaging-btn"
        className={`relative px-3 py-1 transition-colors hidden sm:flex items-center ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`} title="Messages">
        <MessageCircle size={13} />
        <span className="absolute -top-0.5 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border border-white text-[6px] font-bold text-white flex items-center justify-center">3</span>
      </button>

      <Pipe />

      {/* ── Tables ── */}
      <button 
        data-testid="taskbar-tableview-btn"
        onClick={onTableViewToggle}
        className={`flex items-center gap-1 px-3 py-1 text-[10px] font-bold transition-colors touch-target ${
          isTableViewOpen 
            ? 'bg-blue-500 text-white shadow shadow-blue-500/30 rounded-md' 
            : isDark ? 'text-slate-500 hover:text-blue-400' : 'text-slate-400 hover:text-blue-500'
        }`} 
        title="Table Viewer"
      >
        <Layers size={13} />
        <span className="hidden sm:inline">Tables</span>
      </button>

      <Pipe />

      {/* ── SysLog ── */}
      <button 
        data-testid="taskbar-syslog-btn"
        onClick={onSystemLogToggle}
        className={`flex items-center gap-1 px-3 py-1 text-[10px] font-bold transition-colors touch-target ${
          isSystemLogOpen 
            ? 'bg-red-500 text-white shadow shadow-red-500/30 rounded-md' 
            : isDark ? 'text-slate-500 hover:text-red-400' : 'text-slate-400 hover:text-red-500'
        }`} 
        title="System Log"
      >
        <AlertTriangle size={13} />
        <span className="hidden sm:inline">SysLog</span>
      </button>

      <Pipe />

      {/* ── Assistant ── */}
      <button 
        data-testid="taskbar-assistant-btn"
        onClick={onSmartAssistantToggle}
        className={`relative flex items-center gap-1 px-3 py-1 text-[10px] font-bold transition-colors touch-target ${
          isSmartAssistantOpen 
            ? 'bg-amber-500 text-white shadow shadow-amber-500/30 rounded-md' 
            : isDark ? 'text-slate-500 hover:text-amber-400' : 'text-slate-400 hover:text-amber-500'
        }`} 
        title={`Smart Assistant — ${inboxBadge.totalOpen} open · ${inboxBadge.newSinceLogin} since login · ${inboxBadge.newSinceLastOpened} new`}
      >
        <Zap size={13} />
        <span className="hidden sm:inline">Assistant</span>
        {inboxBadge.totalOpen > 0 && (
          <span
            data-testid="assistant-inbox-badge"
            className={`absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] flex items-center justify-center rounded-full text-[8px] font-black px-0.5 pointer-events-none ${
              inboxBadge.newSinceLastOpened > 0 ? 'bg-red-500 text-white animate-pulse' : 'bg-amber-500 text-white'
            }`}
          >
            {inboxBadge.totalOpen > 99 ? '99+' : inboxBadge.totalOpen}
          </span>
        )}
      </button>
      <Toaster position="bottom-right" richColors closeButton />

      <Pipe />

      {/* ── Current User ── */}
      <div className="hidden sm:flex items-center gap-1.5 px-3">
        <User size={12} className={isDark ? 'text-slate-500' : 'text-slate-400'} />
        <span className={`text-[10px] font-bold uppercase ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{userName || 'ADMIN'}</span>
      </div>

      <Pipe />

      {/* ── Wifi ── */}
      <div className={`flex items-center px-3 ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
        <Wifi size={13} />
      </div>

      <Pipe />

      {/* ── Time ── */}
      <div className="flex flex-col items-end min-w-[50px] sm:min-w-[70px] px-3">
        <span data-testid="taskbar-time" className={`font-mono font-bold text-[10px] sm:text-[11px] leading-none ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
          {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
        </span>
        <span className={`font-mono text-[7px] sm:text-[8px] leading-none mt-0.5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
          {time.toLocaleDateString([], { month: 'short', day: 'numeric' })}
        </span>
      </div>
    </div>
  );
};

export default Taskbar;
